/**
 * Portal User Management API Client
 *
 * Handles CRUD operations for portal users with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

// Role display name mapping
export const ROLE_DISPLAY_NAMES = {
  portal_developer: 'Developer',
  portal_system_admin: 'System Admin',
  portal_director: 'Director',
  portal_manager: 'Manager',
  portal_admin: 'Admin',
  portal_teacher: 'Teacher',
  portal_parent: 'Parent'
};

// All portal roles
export const PORTAL_ROLES = [
  'portal_developer',
  'portal_system_admin',
  'portal_director',
  'portal_manager',
  'portal_admin',
  'portal_teacher',
  'portal_parent'
];

// Visible portal roles (excludes system_admin for regular users)
export const VISIBLE_PORTAL_ROLES = PORTAL_ROLES.filter(
  role => role !== 'portal_system_admin' && role !== 'portal_developer'
);

/**
 * Get the authentication token from the user's session.
 * This should be the ID token from Cognito.
 */
const getAuthToken = () => {
  // Try to get token from oidc storage
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * List all users with their roles.
 * Only available to portal_system_admin.
 */
export const listUsers = async () => {
  const result = await apiRequest('list');
  return result.users || [];
};

/**
 * Create a new user.
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {string} email - User's email address
 * @param {string} name - User's full name
 * @param {string} campusId - Required campus ID
 * @param {string} role - Required initial role (e.g., 'portal_teacher')
 * @param {Object} profile - Optional profile data (e.g., { phone: '...' } for staff)
 * @param {string[]} studentIds - Optional array of student IDs to link (for parent role)
 */
export const createUser = async (email, name, campusId, role = '', profile = {}, studentIds = []) => {
  const result = await apiRequest('create', { email, name, campusId, role, profile, studentIds });
  return result.user;
};

/**
 * Update a user's roles.
 * Uses hierarchical permission control.
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {string[]} roles - Array of new role strings (e.g., ['portal_teacher'])
 */
export const updateUserRoles = async (userId, roles, studentIds) => {
  const data = { userId, roles };
  if (studentIds && studentIds.length > 0) {
    data.studentIds = studentIds;
  }
  const result = await apiRequest('updateRoles', data);
  return result;
};

/**
 * Update a user's campus assignment (legacy - replaces all campuses).
 * For multi-campus support, use addUserToCampus/removeUserFromCampus instead.
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {string} campusId - Campus ID to assign (empty string to remove)
 */
export const updateUserCampus = async (userId, campusId) => {
  const result = await apiRequest('updateCampus', { userId, campusId });
  return result;
};

/**
 * Add a campus to a user's campus list (multi-campus support).
 * Director/Manager/Admin can only add their own campus.
 * System Admin can add any campus.
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {string} campusId - Campus ID to add
 */
export const addUserToCampus = async (userId, campusId) => {
  const result = await apiRequest('addCampus', { userId, campusId });
  return result;
};

/**
 * Remove a campus from a user's campus list (multi-campus support).
 * Director/Manager/Admin can only remove their own campus.
 * System Admin can remove any campus.
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {string} campusId - Campus ID to remove
 */
export const removeUserFromCampus = async (userId, campusId) => {
  const result = await apiRequest('removeCampus', { userId, campusId });
  return result;
};

/**
 * Add a teaching campus to a user (teachingCampusIds).
 *
 * Teaching campuses are SEPARATE from management campuses (campusIds): they let a
 * teacher be assigned to teach at extra campuses WITHOUT granting any admin/manager
 * authority there. Any campus may be added.
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {string} campusId - Campus ID to add as a teaching campus
 */
export const addTeachingCampus = async (userId, campusId) => {
  const result = await apiRequest('addTeachingCampus', { userId, campusId });
  return result;
};

/**
 * Remove a teaching campus from a user (teachingCampusIds).
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {string} campusId - Campus ID to remove from teaching campuses
 */
export const removeTeachingCampus = async (userId, campusId) => {
  const result = await apiRequest('removeTeachingCampus', { userId, campusId });
  return result;
};

/**
 * Disable a user's Cognito account.
 * Updates UserPortal status to 'disabled'.
 * Available to portal_system_admin, portal_director, portal_manager.
 *
 * @param {string} userId - User's ID in UserPortal table
 */
export const disableUser = async (userId) => {
  const result = await apiRequest('disableUser', { userId });
  return result;
};

/**
 * Enable a disabled user's Cognito account.
 * Updates UserPortal status to 'active'.
 * Available to portal_system_admin, portal_director, portal_manager.
 *
 * @param {string} userId - User's ID in UserPortal table
 */
export const enableUser = async (userId) => {
  const result = await apiRequest('enableUser', { userId });
  return result;
};

/**
 * Delete a user's Cognito account.
 * Marks UserPortal status as 'deleted' (record is kept for audit).
 * Available to portal_system_admin, portal_director, portal_manager.
 *
 * @param {string} userId - User's ID in UserPortal table
 */
export const deleteUser = async (userId) => {
  const result = await apiRequest('deleteUser', { userId });
  return result;
};

/**
 * Get students linked to a parent user.
 *
 * @param {string} userId - Parent user's ID in UserPortal table
 */
export const getLinkedStudents = async (userId) => {
  const result = await apiRequest('getLinkedStudents', { userId });
  return result.students || [];
};

/**
 * Update the students linked to a parent user.
 *
 * @param {string} userId - Parent user's ID in UserPortal table
 * @param {string[]} studentIds - New complete list of student IDs to link
 */
export const updateParentStudents = async (userId, studentIds) => {
  const result = await apiRequest('updateParentStudents', { userId, studentIds });
  return result;
};

/**
 * Get display name for a role.
 */
export const getRoleDisplayName = (role) => {
  return ROLE_DISPLAY_NAMES[role] || role.replace('portal_', '').replace('_', ' ');
};

/**
 * Get all roles with their display names.
 */
export const getAllRolesWithNames = () => {
  return PORTAL_ROLES.map(role => ({
    value: role,
    label: ROLE_DISPLAY_NAMES[role]
  }));
};

const portalUserApi = {
  listUsers,
  createUser,
  updateUserRoles,
  updateUserCampus,
  addUserToCampus,
  removeUserFromCampus,
  addTeachingCampus,
  removeTeachingCampus,
  disableUser,
  enableUser,
  deleteUser,
  getLinkedStudents,
  updateParentStudents,
  getRoleDisplayName,
  getAllRolesWithNames,
  ROLE_DISPLAY_NAMES,
  PORTAL_ROLES
};

export default portalUserApi;
